import React, { Fragment, useEffect, useState } from "react";
import Dashboard from "../Admin/Dashboard/Dashboard";
import CurrentDate from "../Admin/CurrentDate";
import Loader from "../Admin/Dashboard/Loader";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import baseApi from "../api";
import { useFormik } from "formik";
import * as Yup from "yup";
import "../../index.css";

const isValidHttpUrl = (value) => {
  const raw = String(value).trim();
  if (!raw) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(raw);
    return true;
  } catch {
    try {
      if (raw.startsWith("//")) {
        // eslint-disable-next-line no-new
        new URL(`https:${raw}`);
        return true;
      }
      if (!/^https?:\/\//i.test(raw)) {
        // eslint-disable-next-line no-new
        new URL(`https://${raw}`);
        return true;
      }
    } catch {
      return false;
    }
    return false;
  }
};

const optionalUrl = (message) =>
  Yup.string()
    .nullable()
    .test("url-or-empty", message, (value) => isValidHttpUrl(value || ""));

const EditPageMetaTags = () => {
  const [fetchLoading, setFetchLoading] = useState(true);
  const [data, setData] = useState({});

  const { id } = useParams();

  useEffect(() => {
    setFetchLoading(true);
    axios
      .get(`${baseApi}meta-tags/${id}`)
      .then((res) => {
        setData(res.data.data || {});
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      slug: data.slug || "",
      title: data.title || "",
      keyword: data.keyword || "",
      description: data.description || "",
      image: data.image || "",
      canonicalUrl: data.canonicalUrl || "",
      og: {
        url: data.og?.url || "",
        title: data.og?.title || "",
        description: data.og?.description || "",
        image: data.og?.image || ""
      },
      twitter: {
        url: data.twitter?.url || "",
        title: data.twitter?.title || "",
        description: data.twitter?.description || "",
        image: data.twitter?.image || ""
      },
      headerScripts: data.headerScripts || "",
      footerScripts: data.footerScripts || ""
    },
    validationSchema: Yup.object({
      slug: Yup.string()
        .required("Slug is required")
        .matches(
          /^[a-zA-Z0-9\-_/]+$/,
          "Use letters, numbers, hyphens, slashes, underscores only"
        ),

      title: Yup.string()
        .required("Title is required")
        .max(200, "Max 200 characters"),

      keyword: Yup.string().required("Keywords are required"),

      description: Yup.string()
        .required("Description is required")
        .max(500, "Max 500 characters"),

      image: Yup.string()
        .nullable()
        .test("image", "Invalid image URL", (value) =>
          isValidHttpUrl(value || "")
        ),

      canonicalUrl: optionalUrl("Invalid canonical URL"),

      og: Yup.object({
        url: optionalUrl("Invalid OG URL"),
        title: Yup.string().nullable(),
        description: Yup.string().nullable(),
        image: optionalUrl("Invalid OG image URL")
      }),

      twitter: Yup.object({
        url: optionalUrl("Invalid Twitter URL"),
        title: Yup.string().nullable(),
        description: Yup.string().nullable(),
        image: optionalUrl("Invalid Twitter image URL")
      }),

      headerScripts: Yup.string()
        .max(5000, "Header script too large")
        .nullable(),

      footerScripts: Yup.string()
        .max(5000, "Footer script too large")
        .nullable()
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await axios.post(`${baseApi}meta-tags/`, values, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwtToken")}`
          }
        });
      } catch (err) {
        console.log(err);
      } finally {
        setSubmitting(false);
      }
    }
  });

  if (fetchLoading) {
    return (
      <Fragment>
        <div className="admin_panel">
          <Dashboard />
          <Loader />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="admin_panel">
        <Dashboard />

        <div className="container">
          <div className="col-sm-12">
            <CurrentDate />

            <form onSubmit={formik.handleSubmit} noValidate>
              {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 ? (
                <div className="alert alert-danger" role="alert">
                  Please fix the highlighted fields. Open Graph / Twitter URL or
                  image fields must be valid URLs (or left empty).
                </div>
              ) : null}

              <div className="form-group">
                <label>
                  <h3>Slug</h3>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="slug"
                  {...formik.getFieldProps("slug")}
                />
                {formik.touched.slug && formik.errors.slug ? (
                  <span className="text-danger">{formik.errors.slug}</span>
                ) : null}
              </div>

              <div className="form-group">
                <label>
                  <h3>Title</h3>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  {...formik.getFieldProps("title")}
                />
                {formik.touched.title && formik.errors.title ? (
                  <span className="text-danger">{formik.errors.title}</span>
                ) : null}
              </div>

              <div className="form-group">
                <label>
                  <h3>Keywords</h3>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="keyword"
                  {...formik.getFieldProps("keyword")}
                />
                {formik.touched.keyword && formik.errors.keyword ? (
                  <span className="text-danger">{formik.errors.keyword}</span>
                ) : null}
              </div>

              <div className="form-group">
                <label>
                  <h3>Description</h3>
                </label>
                <textarea
                  className="form-control"
                  name="description"
                  rows="3"
                  {...formik.getFieldProps("description")}
                />
                {formik.touched.description && formik.errors.description ? (
                  <span className="text-danger">{formik.errors.description}</span>
                ) : null}
              </div>

              <div className="form-group">
                <label>
                  <h3>Image</h3>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="image"
                  placeholder="https://"
                  {...formik.getFieldProps("image")}
                />
                {formik.touched.image && formik.errors.image ? (
                  <span className="text-danger">{formik.errors.image}</span>
                ) : null}
              </div>

              <div className="form-group">
                <label>
                  <h3>Canonical URL</h3>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="canonicalUrl"
                  {...formik.getFieldProps("canonicalUrl")}
                />
                {formik.touched.canonicalUrl && formik.errors.canonicalUrl ? (
                  <span className="text-danger">{formik.errors.canonicalUrl}</span>
                ) : null}
              </div>

              <hr />

              <h4>Open Graph (OG)</h4>

              <input
                className="form-control mb-2"
                placeholder="OG URL"
                name="og.url"
                {...formik.getFieldProps("og.url")}
              />
              <input
                className="form-control mb-2"
                placeholder="OG Title"
                name="og.title"
                {...formik.getFieldProps("og.title")}
              />
              <input
                className="form-control mb-2"
                placeholder="OG Description"
                name="og.description"
                {...formik.getFieldProps("og.description")}
              />
              <input
                className="form-control mb-2"
                placeholder="OG Image"
                name="og.image"
                {...formik.getFieldProps("og.image")}
              />
              {formik.touched.og?.image && formik.errors.og?.image ? (
                <p className="text-danger">{formik.errors.og.image}</p>
              ) : null}
              {formik.submitCount > 0 && formik.errors.og?.url ? (
                <p className="text-danger">OG URL: {formik.errors.og.url}</p>
              ) : null}

              <hr />

              <h4>Twitter</h4>

              <input
                className="form-control mb-2"
                placeholder="Twitter URL"
                name="twitter.url"
                {...formik.getFieldProps("twitter.url")}
              />
              <input
                className="form-control mb-2"
                placeholder="Twitter Title"
                name="twitter.title"
                {...formik.getFieldProps("twitter.title")}
              />
              <input
                className="form-control mb-2"
                placeholder="Twitter Description"
                name="twitter.description"
                {...formik.getFieldProps("twitter.description")}
              />
              <input
                className="form-control mb-2"
                placeholder="Twitter Image"
                name="twitter.image"
                {...formik.getFieldProps("twitter.image")}
              />
              {formik.touched.twitter?.image && formik.errors.twitter?.image ? (
                <p className="text-danger">{formik.errors.twitter.image}</p>
              ) : null}
              {formik.submitCount > 0 && formik.errors.twitter?.url ? (
                <p className="text-danger">Twitter URL: {formik.errors.twitter.url}</p>
              ) : null}

              <hr />

              <div className="form-group">
                <label>
                  <h3>Header Scripts</h3>
                </label>
                <textarea
                  className="form-control"
                  name="headerScripts"
                  rows="4"
                  placeholder="<script>...</script>"
                  {...formik.getFieldProps("headerScripts")}
                />
              </div>

              <div className="form-group">
                <label>
                  <h3>Footer Scripts</h3>
                </label>
                <textarea
                  className="form-control"
                  name="footerScripts"
                  rows="4"
                  {...formik.getFieldProps("footerScripts")}
                />
              </div>

              <hr />

              <div className="form-group">
                <input
                  type="submit"
                  className="btn btn-primary"
                  value={formik.isSubmitting ? "Saving…" : "Update Meta Tags"}
                  disabled={formik.isSubmitting}
                />
                &nbsp;&nbsp;
                <Link to="/website-meta-tags">Back to Page Meta Tags</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditPageMetaTags;
